/**
 * Tier Badge Component
 * Displays Buffies tier with icon and color.
 *
 * Supports the four DB tier values plus the LEGEND_DERIVED pseudo-tier
 * (used by UI surfaces that have lifetime-Stars context and want to show
 * "Buffies Legend" for PLATINUM customers at ≥1,600 Stars).
 */

import React from 'react';

export type TierLevel = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'LEGEND_DERIVED';

export interface TierBadgeProps {
  tier: TierLevel;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
  /** Override the default label, e.g. for "Buffies Legend" on lifetime≥1600. */
  label?: string;
}

const TIER_CONFIG: Record<
  TierLevel,
  {
    label: string;
    icon: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  BRONZE: {
    label: 'Buffies Member',
    icon: '⭐',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
  },
  SILVER: {
    label: 'Buffies Crew',
    icon: '🥈',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  },
  GOLD: {
    label: 'Buffies VIP',
    icon: '🥇',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-300',
  },
  PLATINUM: {
    label: 'Buffies Icon',
    icon: '💎',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-300',
  },
  LEGEND_DERIVED: {
    label: 'Buffies Legend',
    icon: '👑',
    bgColor: 'bg-navy-50',
    textColor: 'text-navy-700',
    borderColor: 'border-navy-300',
  },
};

const SIZE_STYLES = {
  sm: {
    padding: 'px-2 py-0.5',
    text: 'text-xs',
    icon: 'text-sm',
    gap: 'gap-1',
  },
  md: {
    padding: 'px-3 py-1',
    text: 'text-sm',
    icon: 'text-base',
    gap: 'gap-1.5',
  },
  lg: {
    padding: 'px-4 py-2',
    text: 'text-base',
    icon: 'text-lg',
    gap: 'gap-2',
  },
};

export const TierBadge: React.FC<TierBadgeProps> = ({
  tier,
  size = 'md',
  showIcon = true,
  className = '',
  label,
}) => {
  const config = TIER_CONFIG[tier] ?? TIER_CONFIG.BRONZE;
  const sizeStyle = SIZE_STYLES[size];

  return (
    <span
      className={`
        inline-flex items-center ${sizeStyle.gap} ${sizeStyle.padding}
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        border rounded-full font-semibold
        ${className}
      `}
    >
      {showIcon && <span className={sizeStyle.icon}>{config.icon}</span>}
      <span className={sizeStyle.text}>{label ?? config.label}</span>
    </span>
  );
};

export default TierBadge;
